import { RouteStop } from '@/new-transport/_models/transport-map.model';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { StopKind } from '@/new-transport/_models/transport.model';
import { deepClone } from '@/_utils/deep-clone';

/**
 * Combines vehicle routes and slots into a unified map by merging route stops with duplicate keys.
 * @param {Map<number, RouteStop[]>} baseRoute - A map of vehicle IDs to route stops.
 * @returns {Map<number, RouteStop[]>} - A new map of vehicle IDs to combined and filtered route stops.
 */
export function combineVehicleRoutesSlots(baseRoute: Map<number, RouteStop[]>): Map<number, RouteStop[]> {
	/**
	 * Creates a composite key for a RouteStop based on its properties.
	 * @param {RouteStop} param0 - An object containing the action, cityId, destinationId, fromCity, and vehicleId properties of a RouteStop.
	 * @returns {string} - A composite key string.
	 */
	const createCompositeKey = ({ action, cityId, destinationId, fromCity, vehicleId }: RouteStop): string => {
		return `${action}-${cityId}-${destinationId}-${fromCity}-${vehicleId}`;
	};

	/**
	 * Merges slots for a RouteStop entry into a temporary map.
	 * @param {Map<string, RouteStop>} tempMap - A temporary map to store merged RouteStops.
	 * @param {RouteStop} entry - The RouteStop entry to be merged.
	 */
	const mergeEntrySlots = (tempMap: Map<string, RouteStop>, entry: RouteStop) => {
		const compositeKey = createCompositeKey(entry);

		if (tempMap.has(compositeKey)) {
			const existingEntry = tempMap.get(compositeKey);
			existingEntry.slots = removeDuplicatesFromArray(existingEntry.slots.concat(entry.slots));
		} else {
			tempMap.set(compositeKey, { ...entry, slots: [...entry.slots] });
		}
	};

	const combinedMap = new Map();

	for (const [vehicleId, entries] of baseRoute.entries()) {
		const tempMap = new Map();

		for (const entry of entries) {
			mergeEntrySlots(tempMap, entry);
		}

		combinedMap.set(vehicleId, Array.from(tempMap.values()));
	}
	return removeBADOPairs(deepClone(combinedMap));
}

/**
 * Filters out boarding and drop-off pairs for each vehicle route.
 * @param {Map<number, RouteStop[]>} map - A map of vehicle IDs to RouteStop arrays.
 * @returns {Map<number, RouteStop[]>} - A new map with filtered RouteStops.
 */
function removeBADOPairs(map: Map<number, RouteStop[]>): Map<number, RouteStop[]> {
	const result = new Map();

	for (const [key, stops] of map.entries()) {
		result.set(key, filterStops(stops));
	}

	return result;
}

/**
 * Filters the provided stops by removing overlapping slots between BOARDING and DROP_OFF actions
 * in the same city and vehicle. For each boarding stop, it removes the slots that are already
 * present in the corresponding drop-off stop.
 *
 * @param {RouteStop[]} stops - Array of route stops to process.
 * @returns {RouteStop[]} - Filtered array of stops with updated slots.
 */
function filterStops(stops: RouteStop[]): RouteStop[] {
	return stops
		.map((stop) => {
			if (stop.action !== StopKind.BOARDING) {
				return stop;
			}

			const dropOffStop = stops.find(
				(s) =>
					s.action === StopKind.DROP_OFF &&
					stop.cityId === s.cityId &&
					stop.fromCity === s.fromCity &&
					stop.destinationId === s.destinationId &&
					stop.vehicleId === s.vehicleId,
			);

			if (dropOffStop) {
				stop.slots = stop.slots.filter((slot) => !dropOffStop.slots.some((v) => v.id === slot.id));
				dropOffStop.slots = null;
			}

			return stop;
		})
		.filter((stop) => stop.slots !== null);
}
